import React, { createContext, useContext, useState, useCallback } from 'react';
import { useAuth } from './authContext';

const BusinessAccessContext = createContext();

export const useBusinessAccess = () => {
  const context = useContext(BusinessAccessContext);
  if (!context) {
    throw new Error('useBusinessAccess must be used within a BusinessAccessProvider');
  }
  return context;
};

export const BusinessAccessProvider = ({ children }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const { makeAuthenticatedRequest, loginType } = useAuth();

  // API base URL for business access endpoints
  const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://7d515f15e81f.ngrok-free.app';
  
  // Determine the correct base URL based on login type
  const getAccessBaseUrl = useCallback(() => {
    if (loginType === 'master') {
      return `${API_BASE_URL}/master/business-access/api/v1/business-access`;
    } else if (loginType === 'admin') {
      return `${API_BASE_URL}/admin/business-access/api/v1/business-access`;
    } else {
      throw new Error('Invalid login type for business access control');
    }
  }, [API_BASE_URL, loginType]);

  // Helper function to handle API responses
  const handleApiResponse = async (response) => {
    const data = await response.json();
    if (!response.ok) {
      throw new Error(data.error || data.message || 'Request failed');
    }
    return data;
  };

  // Clear error state
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  // Validate user access
  const validateAccess = useCallback(() => {
    if (loginType !== 'admin' && loginType !== 'master') {
      throw new Error('Only admin and master users can manage business access');
    }
  }, [loginType]);

  /**
   * Fetches all access control records for a specific business (both admin and master).
   * @param {string} registryNumber The business registry number.
   * @returns {Promise<object>} The API response containing the list of access controls.
   */
  const getAccessControls = useCallback(async (registryNumber) => {
    validateAccess();
    if (!registryNumber) {
      throw new Error('Registry number is required to fetch access controls.');
    }

    setLoading(true);
    setError(null);

    try {
      const baseUrl = getAccessBaseUrl();
      const url = `${baseUrl}/registry/${encodeURIComponent(registryNumber)}`;
      const response = await makeAuthenticatedRequest(url);
      return await handleApiResponse(response);
    } catch (err) {
      const errorMessage = err.message || 'Failed to fetch access controls';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [makeAuthenticatedRequest, validateAccess, getAccessBaseUrl]);

  /**
   * Fetches only the master access control record for a specific business.
   * @param {string} registryNumber The business registry number.
   * @returns {Promise<object>} The master access control record.
   */
  const getMasterAccessControl = useCallback(async (registryNumber) => {
    validateAccess();
    if (!registryNumber) {
      throw new Error('Registry number is required to fetch master access control.');
    }

    setLoading(true);
    setError(null);

    try {
      const baseUrl = getAccessBaseUrl();
      const url = `${baseUrl}/registry/${encodeURIComponent(registryNumber)}/master`;
      const response = await makeAuthenticatedRequest(url);
      return await handleApiResponse(response);
    } catch (err) {
      const errorMessage = err.message || 'Failed to fetch master access control';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [makeAuthenticatedRequest, validateAccess, getAccessBaseUrl]);

  /**
   * Toggles a specific access permission for an admin or master on a business.
   * @param {string} registryNumber The business registry number.
   * @param {string} adminId The ID of the admin whose access is being changed, or "master" for master access.
   * @param {string} accessType The name of the permission field (e.g., 'product_access').
   * @param {boolean} newValue The new boolean value for the permission.
   * @param {boolean} isMaster Whether this is targeting master access (optional, can be inferred from adminId).
   * @returns {Promise<object>} The success message from the API.
   */
  const toggleAccessPermission = useCallback(async (registryNumber, adminId, accessType, newValue, isMaster = false) => {
    validateAccess();
    if (!registryNumber || !accessType) {
      throw new Error('Registry number and access type are required.');
    }

    // Auto-detect master access if adminId is "master" or null
    const isMasterAccess = isMaster || adminId === 'master' || adminId === null || adminId === undefined;
    const targetAdminId = isMasterAccess ? 'master' : adminId;

    if (!isMasterAccess && !adminId) {
      throw new Error('Admin ID is required for admin access control.');
    }

    setLoading(true);
    setError(null);

    try {
      const baseUrl = getAccessBaseUrl();
      const url = `${baseUrl}/registry/${encodeURIComponent(registryNumber)}/admin/${encodeURIComponent(targetAdminId)}`;
      
      const payload = {
        access_type: accessType,
        new_value: newValue,
      };

      // Add is_master flag if explicitly targeting master access
      if (isMasterAccess) {
        payload.is_master = true;
      }

      const response = await makeAuthenticatedRequest(url, {
        method: 'PATCH',
        body: JSON.stringify(payload),
      });
      
      return await handleApiResponse(response);
    } catch (err) {
      const errorMessage = err.message || 'Failed to toggle access permission';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [makeAuthenticatedRequest, validateAccess, getAccessBaseUrl]);

  /**
   * Toggles a specific access permission for master access.
   * Convenience method for master access control.
   * @param {string} registryNumber The business registry number.
   * @param {string} accessType The name of the permission field (e.g., 'product_access').
   * @param {boolean} newValue The new boolean value for the permission.
   * @returns {Promise<object>} The success message from the API.
   */
  const toggleMasterAccessPermission = useCallback(async (registryNumber, accessType, newValue) => {
    return toggleAccessPermission(registryNumber, 'master', accessType, newValue, true);
  }, [toggleAccessPermission]);

  /**
   * Creates a default access control record for the currently authenticated user.
   * All permissions will be enabled.
   * @param {string} registryNumber The business registry number.
   * @param {boolean} createMasterAccess Whether to create master access (default: false for admin access).
   * @returns {Promise<object>} The newly created access control record.
   */
  const createDefaultAccess = useCallback(async (registryNumber, createMasterAccess = false) => {
    validateAccess();
    if (!registryNumber) {
      throw new Error('Registry number is required to create default access.');
    }

    setLoading(true);
    setError(null);

    try {
      const baseUrl = getAccessBaseUrl();
      let url = `${baseUrl}/registry/${encodeURIComponent(registryNumber)}/default`;
      
      // Add query parameter for master access
      if (createMasterAccess) {
        url += '?master=true';
      }
      
      const response = await makeAuthenticatedRequest(url, {
        method: 'POST',
      });
      
      return await handleApiResponse(response);
    } catch (err) {
      const errorMessage = err.message || 'Failed to create default access';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [makeAuthenticatedRequest, validateAccess, getAccessBaseUrl]);

  /**
   * Creates a default master access control record.
   * Convenience method for creating master access.
   * @param {string} registryNumber The business registry number.
   * @returns {Promise<object>} The newly created master access control record.
   */
  const createDefaultMasterAccess = useCallback(async (registryNumber) => {
    return createDefaultAccess(registryNumber, true);
  }, [createDefaultAccess]);

  /**
   * Determines if an access control record is for master access.
   * @param {object} control The access control record.
   * @returns {boolean} True if this is a master access record.
   */
  const isMasterAccessRecord = useCallback((control) => {
    return control && (control.admin_id === null || control.is_master_access === true);
  }, []);

  /**
   * Gets a display name for an access control record.
   * @param {object} control The access control record.
   * @returns {string} A user-friendly display name.
   */
  const getAccessRecordDisplayName = useCallback((control) => {
    if (!control) return 'Unknown';
    
    if (isMasterAccessRecord(control)) {
      return 'Master Access';
    }
    
    return `Admin: ${control.admin_id}`;
  }, [isMasterAccessRecord]);

  /**
   * Formats a raw access control record for easier display.
   * @param {object} control The raw access control object from the API.
   * @returns {object} A formatted object with user-friendly fields.
   */
  const formatAccessData = useCallback((control) => {
    if (!control) return null;

    const permissions = [
      { key: 'public_access', label: 'Public Profile' },
      { key: 'product_access', label: 'Products' },
      { key: 'service_access', label: 'Services' },
      { key: 'employee_access', label: 'Employees' },
      { key: 'event_access', label: 'Events' },
      { key: 'qr_access', label: 'QR Codes' },
      { key: 'location_access', label: 'Locations'},
    ];
    
    const granted = permissions.filter(p => control[p.key]).map(p => p.label);
    const denied = permissions.filter(p => !control[p.key]).map(p => p.label);
    const isMaster = isMasterAccessRecord(control);

    return {
      ...control,
      formattedLastUpdate: new Date(control.last_update).toLocaleString('es-PR', {
        dateStyle: 'medium',
        timeStyle: 'short',
      }),
      grantedPermissions: granted,
      deniedPermissions: denied,
      totalGranted: granted.length,
      allPermissions: permissions.map(p => ({ ...p, value: control[p.key] })),
      isMasterAccess: isMaster,
      displayName: getAccessRecordDisplayName(control),
      userType: isMaster ? 'master' : 'admin',
    };
  }, [isMasterAccessRecord, getAccessRecordDisplayName]);

  /**
   * Separates access controls into master and admin records.
   * @param {Array} controls Array of access control records.
   * @returns {object} Object with masterAccess and adminAccess arrays.
   */
  const separateAccessRecords = useCallback((controls) => {
    if (!Array.isArray(controls)) return { masterAccess: [], adminAccess: [] };
    
    const masterAccess = controls.filter(isMasterAccessRecord);
    const adminAccess = controls.filter(control => !isMasterAccessRecord(control));
    
    return {
      masterAccess,
      adminAccess,
      hasMasterAccess: masterAccess.length > 0,
      adminCount: adminAccess.length,
    };
  }, [isMasterAccessRecord]);

  /**
   * Gets current endpoint info for debugging purposes.
   * @returns {object} Information about the current API endpoint configuration.
   */
  const getEndpointInfo = useCallback(() => {
    const baseUrl = getAccessBaseUrl();
    return {
      loginType,
      baseUrl,
      accessLevel: loginType === 'master' ? 'Master Access' : 'Admin Access',
      requiredHeaders: loginType === 'master' 
        ? ['Authorization', 'secret_credential']
        : ['Authorization', 'admin_id'],
      endpoints: {
        getControls: `${baseUrl}/registry/{registryNumber}`,
        getMasterAccess: `${baseUrl}/registry/{registryNumber}/master`,
        toggleAccess: `${baseUrl}/registry/{registryNumber}/admin/{adminId} (PATCH)`,
        toggleMasterAccess: `${baseUrl}/registry/{registryNumber}/admin/master (PATCH)`,
        createDefaultAccess: `${baseUrl}/registry/{registryNumber}/default (POST)`,
        createMasterAccess: `${baseUrl}/registry/{registryNumber}/default?master=true (POST)`,
      }
    };
  }, [loginType, getAccessBaseUrl]);

  const value = {
    // State
    loading,
    error,
    
    // Core Actions
    getAccessControls,
    getMasterAccessControl,
    toggleAccessPermission,
    toggleMasterAccessPermission,
    createDefaultAccess,
    createDefaultMasterAccess,
    
    // Helpers
    clearError,
    formatAccessData,
    getEndpointInfo,
    isMasterAccessRecord,
    getAccessRecordDisplayName,
    separateAccessRecords,
    
    // Computed values
    isAdminUser: loginType === 'admin',
    isMasterUser: loginType === 'master',
    hasAccessManagement: loginType === 'admin' || loginType === 'master',
  };

  return (
    <BusinessAccessContext.Provider value={value}>
      {children}
    </BusinessAccessContext.Provider>
  );
};